/** @file decodePng: decodes a non-interlaced PNG to RGBA8 with an injected zlib codec. */
import { throwError } from '@zilo/pose3d-std/errors/throwError';
import { concatBytes } from '../bytes/concatBytes';
import { IMAGE_IO_ERRORS } from '../errors/imageIoErrors';
import type { Image } from '../types/image';
import type { ZlibCodec } from '../types/zlibCodec';
import { expandToRgba } from './expandToRgba';
import type { PngHeader } from './pngHeader';
import { readChunks } from './readChunks';
import { readHeader } from './readHeader';
import { unfilterScanlines } from './unfilterScanlines';

/** `maxPixels` caps the declared width × height (default 2^26 = 8192²; a Worker should pass less). */
export type PngDecodeOptions = { maxPixels?: number };

/** Default pixel cap: 8192 × 8192 (256 MB of RGBA). */
const MAX_PIXELS = 2 ** 26;

/** All IDAT payloads joined in file order. */
function joinIdat(chunks: { type: string; data: Uint8Array }[]): Uint8Array {
  const parts = chunks.filter((c) => c.type === 'IDAT').map((c) => c.data);
  if (parts.length === 0) return throwError(IMAGE_IO_ERRORS.PNG_CHUNK_ORDER, { missing: 'IDAT' }, { path: '/png/IDAT' });
  return concatBytes(parts);
}

/** Inflates with the codec; any throw or rejection becomes `PNG_INFLATE` at `/png/IDAT`. */
async function inflate(codec: ZlibCodec, data: Uint8Array): Promise<Uint8Array> {
  try {
    return await codec.inflate(data);
  } catch (e) {
    return throwError(IMAGE_IO_ERRORS.PNG_INFLATE, { cause: e instanceof Error ? e.message : String(e) }, { path: '/png/IDAT' });
  }
}

/** The header of the first chunk, which must be IHDR, non-interlaced and at most `maxPixels` pixels. */
function supportedHeader(first: { type: string; data: Uint8Array } | undefined, maxPixels: number): PngHeader {
  const path = { path: '/png/IHDR' };
  if (first?.type !== 'IHDR') return throwError(IMAGE_IO_ERRORS.PNG_CHUNK_ORDER, { first: first?.type }, path);
  const header = readHeader(first.data);
  if (header.interlace === 1) return throwError(IMAGE_IO_ERRORS.PNG_INTERLACE_UNSUPPORTED, {}, { ...path, hint: 'save non-interlaced' });
  const { width, height } = header;
  if (width * height > maxPixels) return throwError(IMAGE_IO_ERRORS.PNG_TOO_LARGE, { width, height, maxPixels }, path);
  return header;
}

/**
 * RGBA8 of a PNG (every colour type, depths 1–16, filters 0–4, tRNS); IHDR must come first; Adam7 throws
 * `PNG_INTERLACE_UNSUPPORTED`; a declared size over `maxPixels` throws `PNG_TOO_LARGE` before inflating (the codec
 * itself must bound a deflate bomb). The codec is required (`webZlibCodec` on Workers, a node:zlib adapter in tests and the CLI).
 */
export async function decodePng(bytes: Uint8Array, codec: ZlibCodec, options: PngDecodeOptions = {}): Promise<Image> {
  const chunks = readChunks(bytes);
  const header = supportedHeader(chunks[0], options.maxPixels ?? MAX_PIXELS);
  const palette = chunks.find((c) => c.type === 'PLTE')?.data;
  if (header.colorType === 3 && palette === undefined) {
    return throwError(IMAGE_IO_ERRORS.PNG_CHUNK_ORDER, { missing: 'PLTE' }, { path: '/png/PLTE' });
  }
  const trns = chunks.find((c) => c.type === 'tRNS')?.data;
  const rows = unfilterScanlines(await inflate(codec, joinIdat(chunks)), header);
  return expandToRgba(rows, header, palette, trns);
}
